import { useState, useEffect } from "react";
import { Button } from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { booksAPI, inventoryAPI } from "@/services/api";

interface Book {
  id: string;
  name: string;
  stock?: number;
  price?: number;
  version?: number;
  parent_book_id?: string;
}

const InventoryForm = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBookId, setSelectedBookId] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(0);
  const [supplierName, setSupplierName] = useState<string>("");
  const [price, setPrice] = useState<number>(0); // Purchase price field
  const [pricePerBook, setPricePerBook] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);

        // Retrieve client ID from localStorage
        const clientId = localStorage.getItem("clientId");

        if (!clientId) throw new Error("Client ID is not set");

        console.log("Fetching books with client ID:", clientId);
        // Fetch books from the API
        const booksData = await booksAPI.getAll(clientId);

        console.log("Books data received:", booksData);

        if (!Array.isArray(booksData)) {
          throw new Error("Invalid books data format received");
        }

        // Make sure all IDs are consistently treated as strings
        setBooks(
          booksData.map((book) => ({
            id: String(book.id), // Convert ID to string for consistent comparison
            name: book.name,
            stock: Number(book.stock) || 0,
            price: Number(book.price) || 0,
            version: Number(book.version) || 0,
            parent_book_id: book.parent_book_id,
          })),
        );

        setLoading(false);
      } catch (error: any) {
        console.error("Error fetching books: ", error);
        setError(error.message || "Failed to fetch books");
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  // Recalculate total cost whenever quantity or price changes
  useEffect(() => {
    setTotalCost(quantity * price);
  }, [quantity, price]);

  // Auto-fill price when book is selected
  useEffect(() => {
    if (selectedBookId) {
      const selectedBook = books.find((book) => book.id === selectedBookId);

      if (selectedBook && selectedBook.price) {
        setPrice(selectedBook.price); // Set purchase price
        setPricePerBook(selectedBook.price); // Set price per book (can be modified separately)
      }
    }
  }, [selectedBookId, books]);

  // Function to get only parent books (not child versions)
  const getParentBooks = () => {
    const parentBooks = books.filter((book) => !book.parent_book_id);
    console.log("All books:", books.map(b => ({ id: b.id, name: b.name, version: b.version, parent_book_id: b.parent_book_id })));
    console.log("Parent books only:", parentBooks.map(b => ({ id: b.id, name: b.name, version: b.version })));
    return parentBooks;
  };

  const handleSave = async () => {
    if (!selectedBookId) {
      setError("Please select a book");

      return;
    }

    // Find the selected book and handle possible undefined values
    const selectedBook = books.find(
      (book) => String(book.id) === String(selectedBookId),
    );

    if (!selectedBook) {
      setError("Invalid book selection");

      return;
    }

    if (quantity <= 0) {
      setError("Quantity must be greater than zero");

      return;
    }

    if (pricePerBook <= 0) {
      setError("Price per book must be greater than zero");
      return;
    }

    if (price <= 0) {
      setError("Purchase price must be greater than zero");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Retrieve client ID from localStorage
      const clientId = localStorage.getItem("clientId");

      if (!clientId) {
        throw new Error("Client ID is not set");
      }

      // Format current date in ISO format
      const today = new Date();
      const dateString = today.toISOString().split("T")[0]; // YYYY-MM-DD format

      console.log("Creating inventory with data:", {
        book: selectedBook.name,
        bookId: selectedBook.id,
        quantity,
        pricePerBook,
        totalCost,
        date: dateString,
      });

      // Add inventory item using the API with snake_case field names
      const inventoryData = {
        book_id: selectedBook.id,
        book_name: selectedBook.name, // Add book name explicitly
        quantity,
        supplier_name: supplierName,
        price: price, // Use price instead of original_price and purchase_price
        price_per_book: pricePerBook, // Price per book field
        total_cost: totalCost, // Add explicit total cost
        client_id: clientId,
        date: dateString,
      };

      const result = await inventoryAPI.create(inventoryData);

      console.log("Inventory creation response:", result);

      // Reset the form
      setSelectedBookId("");
      setQuantity(0);
      setSupplierName("");
      setPrice(0);
      setPricePerBook(0);
      setTotalCost(0);

      setLoading(false);
      navigate(`/inventory`);
    } catch (error: any) {
      if (error.response) {
        console.error("Backend error:", error.response.data);
      }
      console.error("Error adding inventory: ", error);
      setError(error.message || "Failed to add inventory");
      setLoading(false);
    }
  };

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center gap-4 p-4">
        <div className="max-w-3xl w-full p-6 rounded-lg shadow-lg">
          <h1 className="text-xl font-semibold mb-6 text-center">
            {t("Add Inventory")}
          </h1>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
              <button
                className="absolute top-0 right-0 px-4 py-3"
                onClick={() => setError(null)}
              >
                <span className="text-red-500">×</span>
              </button>
            </div>
          )}

          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="bookSelect"
              >
                {t("Book")}
              </label>
              <select
                required
                className="p-2 border rounded dark:bg-gray-700 dark:text-white"
                disabled={loading}
                id="bookSelect"
                value={selectedBookId}
                onChange={(e) => setSelectedBookId(String(e.target.value))}
              >
                <option value="">{t("Select a Book")}</option>
                {getParentBooks().map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="quantity"
              >
                {t("Quantity")}
              </label>
              <input
                required
                className="p-2 border rounded dark:bg-gray-700 dark:text-white"
                disabled={loading}
                id="quantity"
                min="1"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="supplierName"
              >
                {t("Supplier Name")}
              </label>
              <input
                className="p-2 border rounded dark:bg-gray-700 dark:text-white"
                disabled={loading}
                id="supplierName"
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
              />
            </div>

            <div className="flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="price"
              >
                {t("Purchase Price")}
              </label>
              <input
                required
                className="p-2 border rounded dark:bg-gray-700 dark:text-white"
                disabled={loading}
                id="price"
                min="0.01"
                step="0.01"
                type="number"
                value={price}
                onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                htmlFor="pricePerBook"
              >
                {t("Price Per Book")}
              </label>
              <input
                required
                className="p-2 border rounded dark:bg-gray-700 dark:text-white"
                disabled={loading}
                id="pricePerBook"
                min="0.01"
                step="0.01"
                type="number"
                value={pricePerBook}
                onChange={(e) =>
                  setPricePerBook(parseFloat(e.target.value) || 0)
                }
              />
            </div>

            {/* Total Cost Display - Made more prominent */}
            <div className="col-span-1 sm:col-span-2 flex flex-col">
              <label
                className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                htmlFor="totalCost"
              >
                {t("Total Cost")} {quantity > 0 && price > 0 && (
                  <span className="text-xs text-gray-500">
                    ({quantity} × {price} = {totalCost.toFixed(2)})
                  </span>
                )}
              </label>
              <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600">
                <div className="text-center">
                  <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {totalCost.toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} Rs
                  </span>
                </div>
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2 flex justify-center mt-4">
              <Button
                className="w-full"
                color="primary"
                disabled={loading || !selectedBookId || quantity <= 0 || pricePerBook <= 0 || price <= 0}
                type="submit"
              >
                {loading ? t("Saving...") : t("Save")}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default InventoryForm;
